import React, { Fragment, useState } from 'react';
import { useGetSuggestedAddresses } from '../../features/address-book/hooks/use-get-suggested-addresses';
import { useFeatureFlags } from '../../hooks';
import { isLoggedIn } from '../../utils';
import { FieldType, FormFieldsGeneratorProps } from './form-fields-generator-types';
import { CollapseSection } from './fragments/collapsed-section';
import { transformAddressesToSugest } from './utils/get-addresses-for-autocomplete';
import {
  AddressSelectionState,
  AutocompleteTextField,
  Checkbox,
  CUIText,
  Dropdown,
  TextField,
  Text,
} from '@dotcom/crown-ui';
import styles from './form-fields-generator.module.scss';

const SET_VALUE_OPTIONS = { shouldValidate: true, shouldDirty: true, shouldTouch: true };

export const FormFieldsGenerator = ({ selectedFormFields, register, setValue }: FormFieldsGeneratorProps) => {
  const [userInput, setUserInput] = useState('');
  const { IS_ADDRESS_AUTOCOMPLETE_ENABLED, IS_ADDRESS_DISPLAY_NAME_ENABLED } = useFeatureFlags();
  const { suggestedAddressesData, isLoadingSuggestedAddressesData } = useGetSuggestedAddresses(userInput);
  const transformedSuggestedAddresses = transformAddressesToSugest(suggestedAddressesData?.data.suggestions ?? []);

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setUserInput(e.target.value);
  };

  const setSelectedSuggestion = (selectedSuggestion: AddressSelectionState) => {
    if (setValue && selectedSuggestion) {
      setValue('address_line_1', selectedSuggestion.streetLine, SET_VALUE_OPTIONS);
      setValue('city', selectedSuggestion.city, SET_VALUE_OPTIONS);
      setValue('state_code', selectedSuggestion.state, SET_VALUE_OPTIONS);
      setValue('zip', selectedSuggestion.zip, SET_VALUE_OPTIONS);
    }

    setUserInput('');
  };

  const renderFormField = (field: (typeof selectedFormFields)[number], key: string) => (
    <Fragment key={key}>
      {field.typeOfField === FieldType.DROPDOWN && field.showDropdown && (
        <Dropdown
          options={field.dropdownOptions}
          label={field.label}
          register={register(field.name, field.registerOptions)}
          helperText={field.helperText}
          isError={field.isError}
          className={field.addClass}
          testId={field.testId}
          required={field.required}
        />
      )}
      {field.typeOfField === FieldType.TEXTFIELD && (
        <TextField
          register={register(field.name, field.registerOptions)}
          domId={field.domId}
          label={field.label}
          isError={field.isError}
          helperText={field.helperText}
          className={field.addClass}
          testId={field.testId}
          required={field.required}
          onChange={field.onChange}
        />
      )}
      {field.underMessage && (
        <Text as="p" className={styles['form-field-under-message']} color={'textDisabled'} styleVariant={'caption'}>
          {field.underMessage}
        </Text>
      )}
      {field.typeOfField === FieldType.AUTOCOMPLETE && IS_ADDRESS_AUTOCOMPLETE_ENABLED && (
        <>
          <AutocompleteTextField
            register={register(field.name, field.registerOptions)}
            domId={field.domId}
            label={field.label}
            isError={field.isError}
            helperText={field.helperText}
            className={field.addClass}
            testId={field.testId}
            required={field.required}
            onChange={onChange}
            enableAutocomplete={transformedSuggestedAddresses && transformedSuggestedAddresses.length > 0}
            autocompleteData={transformedSuggestedAddresses}
            onSuggestedAddressClick={setSelectedSuggestion}
          />
          {isLoadingSuggestedAddressesData && (
            <CUIText color="textPrimary" as="span" data-testid="autocomplete-loading">
              Loading addresses...
            </CUIText>
          )}
        </>
      )}
      {field.typeOfField === FieldType.CHECKBOX && isLoggedIn() && (
        <Checkbox
          register={register(field.name, field.registerOptions)}
          domId={field.domId}
          helperText={field.helperText}
          className={field.addClass}
          testId={field.testId}
          required={field.required}
        >
          {field.label}
        </Checkbox>
      )}
    </Fragment>
  );

  return (
    <Fragment>
      {selectedFormFields.map(
        (field, index) =>
          field !== undefined &&
          (field.typeOfField === FieldType.COLLAPSE && field.nestedFields !== undefined ? (
            <CollapseSection key={`collapse_${index}`}>
              {field.nestedFields.map((nestedField) =>
                renderFormField(nestedField as (typeof selectedFormFields)[number], `${nestedField.name}_${index}`),
              )}
            </CollapseSection>
          ) : field.typeOfField === FieldType.GROUP && field.nestedFields !== undefined ? (
            <div className={styles['form-field-row']} data-testid="form-field-row" key={`group_${index}`}>
              {field.nestedFields.map((nestedField) =>
                renderFormField(nestedField as (typeof selectedFormFields)[number], `${nestedField.name}_${index}`),
              )}
            </div>
          ) : !IS_ADDRESS_DISPLAY_NAME_ENABLED && field.name === 'display_name' ? null : (
            renderFormField(field, `${field.name}_${index}`)
          )),
      )}
    </Fragment>
  );
};
